import * as React from 'react';
import { WrappedFieldProps } from 'redux-form';
import { FormControlProps } from '@mui/material/FormControl';

export type Mix<P> = P & WrappedFieldProps;

export type FieldClass<P> = React.ClassType<
  P,
  React.Component<P>,
  React.ComponentClass<P>
>;

export type MapProps<P> = (p: Mix<P>) => P;

const isStateLess = (Component: React.ComponentClass) =>
  !Component.prototype.render;

// The idea of mapping MUI components to redux-form components
// took from this library: https://github.com/erikras/redux-form-material-ui
export function fromMUIComponent<P extends object>(
  MaterialUIComponent: FieldClass<P>,
  mapProps: MapProps<Readonly<P>>,
) {
  class InputComponent extends React.Component<Mix<P>> {
    component: React.Component<P> | null;

    // eslint-disable-next-line react/no-unused-class-component-methods
    getRenderedComponent() {
      return !isStateLess(MaterialUIComponent as any) ? this.component : null;
    }

    render() {
      return React.createElement<
        P,
        React.Component<P>,
        React.ComponentClass<P>
      >(
        MaterialUIComponent,
        // dispabling using spread operator here because TS doesn't
        // support typing for spread generics https://github.com/Microsoft/TypeScript/issues/10727
        // eslint-disable-next-line prefer-object-spread
        Object.assign({}, mapProps(this.props), {
          ref: (el: React.Component<P>) => (this.component = el),
        }),
      );
    }
  }

  return InputComponent as React.ComponentClass<Mix<FormControlProps & P>> &
    typeof InputComponent;
}
